/**
 * Offline Reading History Hook
 * Provides access to cached reading history with authentication-based access control
 * Requirements: 9.6, 9.7
 */

import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { offlineReadingHistoryCache } from '../services/offlineReadingHistoryCache';
import { useOfflineAuth } from './useOfflineAuth';
import { useReadings } from './useReadings';
import type { TarotReading } from '../types/tarot';

interface OfflineReadingHistoryState {
  // Access control
  canAccessOffline: boolean;
  accessReason: string;
  isOfflineMode: boolean;
  
  // Data
  cachedReadings: TarotReading[];
  onlineReadings: TarotReading[];
  displayReadings: TarotReading[]; // Combined or cached based on online status
  
  // Metadata
  cacheMetadata: {
    userId: string;
    readingCount: number;
    lastUpdated: number;
    cacheSize: number;
    isStale: boolean;
  } | null;
  
  // Status indicators
  isUsingCache: boolean;
  isCacheStale: boolean;
  hasOfflineChanges: boolean;
  
  // Actions
  refreshCache: () => Promise<void>;
  updateCachedReading: (readingId: string, updates: Partial<TarotReading>) => Promise<boolean>;
  removeCachedReading: (readingId: string) => Promise<boolean>;
  clearCache: () => void;
  
  // Loading states
  isLoadingCache: boolean;
  isLoadingOnline: boolean;
  cacheError: string | null;
}

/**
 * Hook for managing offline reading history access
 */
export const useOfflineReadingHistory = (): OfflineReadingHistoryState => {
  const [isLoadingCache, setIsLoadingCache] = useState(false);
  const [cacheError, setCacheError] = useState<string | null>(null);
  const [cachedReadings, setCachedReadings] = useState<TarotReading[]>([]);
  const [cacheMetadata, setCacheMetadata] = useState<OfflineReadingHistoryState['cacheMetadata']>(null);
  const [canAccessOffline, setCanAccessOffline] = useState(false);
  const [accessReason, setAccessReason] = useState('');
  const [isUsingCache, setIsUsingCache] = useState(false);

  const queryClient = useQueryClient();
  const { isOnline, canAccessReadingHistory, userId } = useOfflineAuth();
  
  // Online readings query (only when online) - fetch all readings for caching
  const { 
    data: onlineReadingsData, 
    isLoading: isLoadingOnline
  } = useReadings({ limit: 1000 }, {
    enabled: isOnline && canAccessReadingHistory
  });

  const onlineReadings = onlineReadingsData?.data?.readings || [];

  /**
   * Load cached reading history
   */
  const loadCachedReadings = useCallback(async () => {
    if (!userId) {
      setCanAccessOffline(false);
      setAccessReason('No user ID available');
      return;
    }

    setIsLoadingCache(true);
    setCacheError(null);

    try {
      const access = await offlineReadingHistoryCache.getCachedReadingHistory();
      
      setCanAccessOffline(access.canAccess);
      setAccessReason(access.reason);
      setCachedReadings(access.cachedReadings);
      setCacheMetadata(access.metadata);
      
      if (!access.canAccess) {
        setCacheError(access.reason);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load cached readings';
      setCacheError(errorMessage);
      setCanAccessOffline(false);
      setAccessReason(errorMessage);
    } finally {
      setIsLoadingCache(false);
    }
  }, [userId]);

  /**
   * Cache online readings when available
   */
  const cacheOnlineReadings = useCallback(async () => {
    if (!userId || !isOnline || !onlineReadings.length) {
      return;
    }

    try {
      const totalCount = onlineReadingsData?.data?.total || onlineReadings.length;
      await offlineReadingHistoryCache.cacheReadingHistory(userId, onlineReadings, totalCount);
      
      // Refresh cached readings after successful cache
      await loadCachedReadings();
    } catch (error) {
      console.error('Failed to cache online readings:', error);
    }
  }, [userId, isOnline, onlineReadings, onlineReadingsData, loadCachedReadings]);

  /**
   * Refresh cache from online data
   */
  const refreshCache = useCallback(async () => {
    if (isOnline) {
      // Invalidate and refetch online data
      await queryClient.invalidateQueries({ queryKey: ['readings'] });
      // Caching will happen automatically when new data arrives
    } else {
      // Just reload cached data
      await loadCachedReadings();
    }
  }, [isOnline, queryClient, loadCachedReadings]);

  /**
   * Update a cached reading (for offline modifications)
   */
  const updateCachedReading = useCallback(async (
    readingId: string, 
    updates: Partial<TarotReading>
  ): Promise<boolean> => {
    const success = await offlineReadingHistoryCache.updateCachedReading(readingId, updates);
    
    if (success) {
      // Reload cached readings to reflect changes
      await loadCachedReadings();
    }
    
    return success;
  }, [loadCachedReadings]);

  /**
   * Remove a cached reading (for offline deletions)
   */
  const removeCachedReading = useCallback(async (readingId: string): Promise<boolean> => {
    const success = await offlineReadingHistoryCache.removeCachedReading(readingId);
    
    if (success) {
      // Reload cached readings to reflect changes
      await loadCachedReadings();
    }
    
    return success;
  }, [loadCachedReadings]);

  /**
   * Clear cache
   */
  const clearCache = useCallback(() => {
    if (userId) {
      offlineReadingHistoryCache.clearCacheForUser(userId);
      setCachedReadings([]);
      setCacheMetadata(null);
      setCanAccessOffline(false);
      setAccessReason('Cache cleared');
    }
  }, [userId]);

  // Load cached readings on mount and when user changes
  useEffect(() => {
    loadCachedReadings();
  }, [loadCachedReadings]);

  // Cache online readings when they become available
  useEffect(() => {
    if (isOnline && onlineReadings.length > 0) {
      cacheOnlineReadings();
    }
  }, [isOnline, onlineReadings, cacheOnlineReadings]);

  // Determine which readings to display and current status
  const displayReadings = isOnline ? onlineReadings : cachedReadings;
  const isOfflineMode = !isOnline;
  const isCacheStale = cacheMetadata?.isStale || false;
  const hasOfflineChanges = false; // TODO: Implement offline change tracking

  // Update isUsingCache based on current state
  useEffect(() => {
    setIsUsingCache(!isOnline && canAccessOffline && cachedReadings.length > 0);
  }, [isOnline, canAccessOffline, cachedReadings.length]);

  return {
    // Access control
    canAccessOffline,
    accessReason,
    isOfflineMode,
    
    // Data
    cachedReadings,
    onlineReadings,
    displayReadings,
    
    // Metadata
    cacheMetadata,
    
    // Status indicators
    isUsingCache,
    isCacheStale,
    hasOfflineChanges,
    
    // Actions
    refreshCache,
    updateCachedReading,
    removeCachedReading,
    clearCache,
    
    // Loading states
    isLoadingCache,
    isLoadingOnline,
    cacheError
  };
};

/**
 * Hook for getting offline reading history statistics
 */
export const useOfflineReadingHistoryStats = () => {
  const [stats, setStats] = useState({
    totalCaches: 0,
    totalReadings: 0,
    totalCacheSize: 0,
    oldestCache: null as number | null,
    newestCache: null as number | null
  });
  const [isLoading, setIsLoading] = useState(false);

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const cacheStats = await offlineReadingHistoryCache.getCacheStatistics();
      setStats(cacheStats);
    } catch (error) {
      console.error('Failed to load cache statistics:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return {
    stats,
    isLoading,
    refresh: loadStats
  };
};

/**
 * Hook for managing cache cleanup
 */
export const useOfflineReadingHistoryCleanup = () => {
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [needsCleanup, setNeedsCleanup] = useState(false);

  const checkCleanupNeeded = useCallback(async () => {
    try {
      const needs = await offlineReadingHistoryCache.needsCleanup();
      setNeedsCleanup(needs);
    } catch (error) {
      console.error('Failed to check cleanup status:', error);
    }
  }, []);

  const performCleanup = useCallback(async () => {
    setIsCleaningUp(true);
    try {
      await offlineReadingHistoryCache.performCleanup();
      setNeedsCleanup(false);
    } catch (error) {
      console.error('Failed to perform cleanup:', error);
    } finally {
      setIsCleaningUp(false);
    }
  }, []);

  useEffect(() => {
    checkCleanupNeeded();
    
    // Check cleanup status periodically
    const interval = setInterval(checkCleanupNeeded, 60000); // Every minute
    return () => clearInterval(interval);
  }, [checkCleanupNeeded]);

  return {
    needsCleanup,
    isCleaningUp,
    performCleanup,
    checkCleanupNeeded
  };
};